import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Container,
    Modal,
    Paper,
    Typography,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

import {Company} from "../types";
import {getCompanyById} from "../api/api";
import {useAlert} from "../context/AlertContext";
import CompanyUsersList from "../components/CompanyUsersList";
import CompanyUserForm from "../components/CompanyUserForm";
import BreadcrumbLink from "../components/BreadcrumbLink";

const getInitials = (name?: string) => {
    if (!name || !name.trim()) return "?";

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const AdminCompanyDetailsPage = () => {
    const {t} = useTranslation();

    const {companyId} = useParams();
    const {showAlert} = useAlert();

    const id = Number(companyId);

    const [company, setCompany] = useState<Company | null>(null);
    const [loading, setLoading] = useState(false);

    const [openUserModal, setOpenUserModal] = useState(false);
    const [refreshUsers, setRefreshUsers] = useState(false);

    const refreshCompanyUsers = () => {
        setRefreshUsers((prev) => !prev);
    };

    const handleError = (error: any) => {
        const msg =
            typeof error === "string"
                ? error
                : error?.message ?? t("admincompanydetails.errors.unexpected");

        showAlert(msg, "error");
    };

    const handleUserCreated = () => {
        setOpenUserModal(false);
        refreshCompanyUsers();
        showAlert(t("admincompanydetails.alerts.userCreated"), "success");
    };

    useEffect(() => {
        const fetchCompany = async () => {
            if (!id) {
                showAlert(t("admincompanydetails.errors.invalidCompanyId"), "error");
                return;
            }

            try {
                setLoading(true);
                const data = await getCompanyById(id);
                setCompany(data);
            } catch (error: any) {
                showAlert(error?.message ?? t("admincompanydetails.errors.loadCompany"), "error");
            } finally {
                setLoading(false);
            }
        };

        fetchCompany();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) {
        return (
            <Container>
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    if (!company) {
        return (
            <Container>
                <Typography color="text.secondary">
                    {t("admincompanydetails.companyNotFound")}
                </Typography>
            </Container>
        );
    }

    return (
        <Box sx={{px: {xs: 1.5, md: 3}}}>
            <Box sx={{mb: 3}}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small"/>} sx={{mb: 2}}>
                    <BreadcrumbLink to="/companies">
                        {t("admincompanydetails.companies")}
                    </BreadcrumbLink>

                    <Typography color="text.primary" variant="body2" sx={{fontWeight: 600}}>
                        {t("admincompanydetails.companyDetails")}
                    </Typography>
                </Breadcrumbs>

                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 3,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            px: {xs: 1.5, md: 3},
                            py: {xs: 2, md: 2.5},
                            borderBottom: `1px solid ${theme.palette.action.hover}`,
                            display: "flex",
                            alignItems: {xs: "stretch", sm: "flex-start"},
                            justifyContent: "space-between",
                            flexDirection: {xs: "column", sm: "row"},
                            gap: 2,
                            background:
                                theme.palette.mode === "dark"
                                    ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                    : "linear-gradient(135deg, rgba(248,250,252,1), rgba(255,255,255,1))",
                        })}
                    >
                        <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0}}>
                            <Box
                                sx={(theme) => ({
                                    width: {xs: 48, md: 54},
                                    height: {xs: 48, md: 54},
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: {xs: 14, md: 16},
                                    fontWeight: 900,
                                    bgcolor: theme.palette.action.hover,
                                    color: theme.palette.text.primary,
                                    flexShrink: 0,
                                    border: `1px solid ${theme.palette.divider}`,
                                })}
                            >
                                {getInitials(company.name)}
                            </Box>

                            <Box sx={{minWidth: 0}}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: {xs: 22, md: 26},
                                        lineHeight: 1.2,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {company.name}
                                </Typography>

                                <Box
                                    sx={{
                                        mt: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 0.75,
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        icon={<BadgeOutlinedIcon sx={{fontSize: 15}}/>}
                                        label={`${t("admincompanydetails.companyId")}: ${company.id}`}
                                        sx={(theme) => ({
                                            fontWeight: 800,
                                            backgroundColor: theme.palette.action.hover,
                                        })}
                                    />

                                    <Chip
                                        size="small"
                                        label={company.active ? t("admincompanydetails.active") : t("admincompanydetails.inactive")}
                                        color={company.active ? "success" : "default"}
                                        variant={company.active ? "filled" : "outlined"}
                                        sx={{fontWeight: 800}}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={(theme) => ({
                                display: {xs: "none", md: "flex"},
                                width: 46,
                                height: 46,
                                borderRadius: "50%",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: theme.palette.action.hover,
                                flexShrink: 0,
                            })}
                        >
                            <BusinessIcon sx={{fontSize: 24}}/>
                        </Box>
                    </Box>

                    <Box sx={{p: {xs: 1.5, md: 3}}}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", md: company.metadataSettings ? "1fr 1fr" : "1fr"},
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={(theme) => ({
                                    p: {xs: 1.5, md: 2},
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.background.paper,
                                })}
                            >
                                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                    <DescriptionOutlinedIcon sx={{fontSize: 20, color: "text.secondary"}}/>

                                    <Typography variant="subtitle2" sx={{fontWeight: 900}}>
                                        {t("admincompanydetails.description")}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.7,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {company.description || t("admincompanydetails.noDescription")}
                                </Typography>
                            </Box>

                            {company.metadataSettings ? (
                                <Box
                                    sx={(theme) => ({
                                        p: {xs: 1.5, md: 2},
                                        borderRadius: 3,
                                        border: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.background.paper,
                                        minWidth: 0,
                                    })}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                        <DataObjectIcon sx={{fontSize: 20, color: "text.secondary"}}/>

                                        <Typography variant="subtitle2" sx={{fontWeight: 900}}>
                                            {t("admincompanydetails.metadataSettings")}
                                        </Typography>
                                    </Box>

                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            p: {xs: 1.5, md: 2},
                                            bgcolor: "#111827",
                                            color: "#f9fafb",
                                            borderRadius: 2,
                                            overflow: "auto",
                                            maxHeight: {xs: 260, md: 360},
                                            fontSize: {xs: 12, md: 13},
                                            lineHeight: 1.55,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            fontFamily:
                                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                        }}
                                    >
                                        {company.metadataSettings}
                                    </Box>
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                </Paper>

                <CompanyUsersList
                    companyId={id}
                    refreshTrigger={refreshUsers}
                    refreshUsers={refreshCompanyUsers}
                    onAdd={() => setOpenUserModal(true)}
                />

                <Modal open={openUserModal} onClose={() => setOpenUserModal(false)}>
                    <>
                        <CompanyUserForm
                            companyId={id}
                            onClose={() => setOpenUserModal(false)}
                            onSuccess={handleUserCreated}
                            onError={handleError}
                        />
                    </>
                </Modal>
            </Box>
        </Box>
    );
};

export default AdminCompanyDetailsPage;